import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { BudgetVersionType } from '@callmart/types';
import { useCallback, useEffect, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BucketVersionFields from '../../components/Budget/BucketVersionFields';
import ConsolidatedScenarioGrid from '../../components/Budget/ConsolidatedScenarioGrid';
import LoadError from '../../components/Budget/LoadError';
import { buildConsolidatedBoards } from '../../components/Budget/consolidatedBoards';
import { BudgetRoutes } from '../../enums/BudgetRoutes';
import { useExportBudgetScenarios, useScenarioConsolidated } from '../../hooks/api/budgetScenarioConsolidated';
import { useBucketScenarios } from '../../hooks/api/budgetScenarios';
import { BudgetScenario } from '../../services/budgetScenario';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useTitleContext } from '../../store/Page/TitleContext';

const SCENARIOS_PARAM = 'scenarios';

// CALL-765: the Konsolidierte Ansicht. Baseline budget (bucket + version) and the scenarios to overlay
// are picked here, one above the other as the board has them. The selection lives in ?scenarios= — a
// per-view choice, so two users can consolidate different combinations and a shared link reopens one
// exact board. Read-only — no baseline data is mutated here.
const BudgetScenariosConsolidated = () => {
    const { t } = useTranslation('budget');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { onChange: changeTitle } = useTitleContext();
    const { selectedVersionId, setSelectedVersionId, selectedVersion } = useBudgetContext();

    // Only bucket-mates can be overlaid: scenario input rows are keyed to bucket entities (worktypes /
    // HEMT locations / billing configs), so a foreign bucket's scenario would contribute zeros while
    // looking counted. Another VERSION of the same bucket is fine — impacts are euro deltas.
    //
    // CALL-794: asked of the SERVER (?bucketId=), which used to send every bucket's scenarios for this
    // page to discard. The filter below stays: the query is keyed per bucket, so a version switch leaves
    // the previous bucket's rows readable for one render before the new key's `initialData: []` takes
    // over, and `areCandidatesKnown` must not see those as this bucket's answer.
    const bucketId = selectedVersion?.budgetBucketId;
    const [scenarios, areScenariosLoading, scenariosState] = useBucketScenarios(bucketId);

    const candidates = useMemo(() => {
        if (bucketId == null) return [];
        return scenarios.filter((scenario) => scenario.version?.budgetBucketId === bucketId);
    }, [scenarios, bucketId]);

    // An EMPTY `candidates` only means "this bucket offers nothing" once both inputs behind it have really
    // answered. Two states produce the same `[]` for reasons that have nothing to do with the selection, and
    // both used to reach the effects below — which write with `replace: true`, so a wiped `?scenarios=` had no
    // Back to recover it:
    //  - a FAILED scenarios query: useBucketScenarios keeps its `initialData: []` and reports
    //    `isFetchedAfterMount ? isLoading : isFetching`; react-query flips isFetchedAfterMount on a REJECTED
    //    fetch too, so the flag becomes isLoading — false — with nothing loaded;
    //  - the versions query losing the race against the scenarios one: `selectedVersion`, and with it the
    //    bucket every candidate is filtered by, is undefined while the scenarios are already in.
    // Neither is distinguishable from "the user deselected everything", so neither may be written.
    const areCandidatesKnown = !areScenariosLoading && !scenariosState.isError && bucketId != null;

    // `?scenarios=` present but empty means an explicit "nothing selected", which must survive a
    // reload — so the absent param (first visit) is what triggers the default below, not an empty one.
    const scenariosParam = searchParams.get(SCENARIOS_PARAM);
    const selectedIds = useMemo(() => {
        if (scenariosParam == null) return null;
        const ids = scenariosParam
            .split(',')
            .map((part) => parseInt(part, 10))
            .filter((id) => Number.isInteger(id) && id > 0);
        return [...new Set(ids)];
    }, [scenariosParam]);

    const writeSelection = useCallback(
        (ids: number[]) => {
            // Sorted so the URL — and with it the query cache key — is canonical: the same set reached
            // by a different route of clicks is one shareable link and one cache entry, not several.
            const sorted = [...ids].sort((a, b) => a - b);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set(SCENARIOS_PARAM, sorted.join(','));
                    return next;
                },
                // The selection is a view parameter, not a navigation step: pushing every tick of the
                // multiselect would bury the page the user came from under a dozen history entries.
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // What "consolidate this budget" means without further input: the chosen version's own scenarios.
    const ownScenarioIds = useMemo(
        () => candidates.filter((s) => s.budgetVersionId === selectedVersionId).map((s) => s.id),
        [candidates, selectedVersionId]
    );

    // First visit to a version: preselect its own scenarios and write them to the URL so the state is
    // shareable from the start. Waits for the candidate list for the same reason as the prune below — an
    // early `[]` here writes an EXPLICITLY empty selection, which the rules then honour for good.
    useEffect(() => {
        if (selectedIds != null || selectedVersionId == null || !areCandidatesKnown) return;
        writeSelection(ownScenarioIds);
    }, [selectedIds, selectedVersionId, areCandidatesKnown, ownScenarioIds, writeSelection]);

    // Ids the current bucket no longer offers (version switched to another bucket, scenario deleted or
    // re-pointed) are dropped from the request: the backend ignores them anyway, and keeping them in
    // the URL would make a stale link look like it selected more than it consolidates.
    const effectiveIds = useMemo(() => {
        if (selectedIds == null) return [];
        const available = new Set(candidates.map((s) => s.id));
        return selectedIds.filter((id) => available.has(id));
    }, [selectedIds, candidates]);

    // CALL-794: the board is only worth requesting once the SELECTION has settled, not merely the
    // version. Three states look like a selection without being one, and each of them sent a board
    // request that the very next render replaced:
    //  - `selectedIds` is null until the URL carries one — on a first visit the default above is written
    //    a render later, so the ids go out as [];
    //  - the candidate list has not answered, so the pruning below is an artefact of an empty list
    //    rather than a real answer (a shared ?scenarios= link collapses to [] on its first render);
    //  - the prune is real but not yet written back — the effect below is about to rewrite the URL, so
    //    this render's ids are already superseded. That is the version switch: crossing into another
    //    bucket invalidates every selected id at once.
    // The last clause is the same guard the prune effect uses to decide it has work to do.
    const isSelectionResolved = areCandidatesKnown && selectedIds != null && effectiveIds.length === selectedIds.length;

    const [result, isLoading, { isError, error, refetch }] = useScenarioConsolidated(
        selectedVersionId,
        effectiveIds,
        isSelectionResolved
    );
    const { mutate: exportScenarios, isLoading: isExporting } = useExportBudgetScenarios();

    // Prune the URL once the pruning is known to be real (the candidate list has loaded), so a copied
    // link keeps matching the board it opens.
    //
    // Losing the WHOLE selection means the bucket changed under it — every id belonged to the previous
    // bucket. Falling back to an empty board there would read as "switching budget does nothing", so it
    // re-resolves the default for the new version, exactly as a first visit would. A selection the user
    // emptied on purpose is already `[]` and stays untouched.
    useEffect(() => {
        if (selectedIds == null || !areCandidatesKnown) return;
        if (effectiveIds.length === selectedIds.length) return;
        writeSelection(effectiveIds.length === 0 && selectedIds.length > 0 ? ownScenarioIds : effectiveIds);
    }, [selectedIds, effectiveIds, areCandidatesKnown, ownScenarioIds, writeSelection]);

    // Without an own menu entry the AppBar would fall back to "Scenarios" — name the view instead.
    useEffect(() => {
        changeTitle(t('consolidated.title'));
        return () => {
            changeTitle(undefined);
        };
    }, [changeTitle, t]);

    const selectedScenarios = useMemo(
        () => candidates.filter((scenario) => effectiveIds.includes(scenario.id)),
        [candidates, effectiveIds]
    );

    const optionLabel = useCallback(
        (scenario: BudgetScenario) => {
            // Which version a scenario belongs to matters here (a bucket-mate on another version is
            // selectable), and whether that version is a released snapshot matters with it.
            const version = scenario.version?.name ?? `#${scenario.budgetVersionId}`;
            const released =
                scenario.version?.type === BudgetVersionType.SNAPSHOT ? ` (${t('version.statusReleased')})` : '';
            return `${scenario.name} · ${t(`scenarioType.${scenario.type}`)} · ${version}${released}`;
        },
        [t]
    );

    const boards = useMemo(() => buildConsolidatedBoards(result, t), [result, t]);

    return (
        <Box>
            <Button
                variant="text"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(BudgetRoutes.SCENARIOS)}
                sx={{ mb: 1 }}
            >
                <span>{t('scenarioDetail.backToOverview')}</span>
            </Button>

            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                    <span>{t('consolidated.title')}</span>
                </Typography>
                {/* CALL-777: the scenarios workbook. This is the only scenarios page with a selected
                    baseline version, so the export lives here rather than on the overview. It carries
                    the same selection as the board. */}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    disabled={selectedVersionId == null || isExporting}
                    onClick={() =>
                        selectedVersionId != null &&
                        exportScenarios({ versionId: selectedVersionId, scenarioIds: effectiveIds })
                    }
                    sx={{ ml: 'auto' }}
                >
                    <span>{isExporting ? t('export.inProgress') : t('consolidated.exportButton')}</span>
                </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <span>{t('consolidated.hint')}</span>
            </Typography>

            {/* Budget first, then the scenarios to overlay — the board's order, and the same two-step
                budget picker the scenario form uses. Inline rather than in the Budget page header, so the
                baseline and the selection read as one choice. */}
            <Box sx={{ maxWidth: 720, mb: 2 }}>
                <BucketVersionFields
                    versionId={selectedVersionId ?? ''}
                    onChange={(versionId) => setSelectedVersionId(versionId === '' ? undefined : versionId)}
                />
            </Box>

            {selectedVersionId != null && (
                <Autocomplete
                    multiple
                    size="small"
                    limitTags={4}
                    disableCloseOnSelect
                    options={candidates}
                    value={selectedScenarios}
                    loading={areScenariosLoading}
                    getOptionLabel={optionLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_event, value) => writeSelection(value.map((scenario) => scenario.id))}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {/* Scenario names and version labels are data — keep translation
                                extensions away from text React re-renders in place. */}
                            <span className="notranslate">{optionLabel(option)}</span>
                        </li>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                size="small"
                                label={<span className="notranslate">{option.name}</span>}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField {...params} label={t('consolidated.scenarioSelect')} placeholder="" />
                    )}
                    sx={{ maxWidth: 720, mb: 2 }}
                />
            )}

            <Divider sx={{ mb: 2 }} />

            {selectedVersionId == null ? (
                <Alert severity="info">
                    <span>{t('consolidated.selectVersion')}</span>
                </Alert>
            ) : isError ? (
                <LoadError error={error} onRetry={refetch} />
            ) : scenariosState.isError ? (
                <LoadError error={scenariosState.error} onRetry={scenariosState.refetch} />
            ) : !result ? (
                // The first board of a session takes the server seconds to assemble, and every table
                // below is built FROM it — so without this the page shows nothing at all under the
                // divider for that whole time and reads as broken. Later selections keep the previous
                // board (keepPreviousData) and get the grids' own loading overlay instead.
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={3}>
                    {boards.map((board) => (
                        <ConsolidatedScenarioGrid
                            key={board.titleKey}
                            titleKey={board.titleKey}
                            rows={board.rows}
                            months={result?.months ?? []}
                            isLoading={isLoading}
                            emptyHintKey={
                                board.isAdjustmentBoard && effectiveIds.length === 0
                                    ? 'consolidated.noScenarios'
                                    : undefined
                            }
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default BudgetScenariosConsolidated;
